import {createApi} from '@reduxjs/toolkit/dist/query/react'
import {baseQueryWithReauth} from './BaseQueryReauth'
import {IAccountUser, IAccountRole} from '../types/users/UserModel'

interface IQueryParams {
  accountId: string
  skip?: number
  limit?: number
}

interface ListResponse<T> {
  data: T[]
  total: number
}

export interface IAccountMemberCreate {
  user_id: string
  role: IAccountRole
  internal_numbers?: string[]
}

export interface IAccountMemberUpdate {
  role?: IAccountRole
  internal_numbers?: string[]
}

export interface IAccountInviteItem {
  email: string
  role: IAccountRole
  internal_numbers?: string[]
}

export interface IAccountInviteResult {
  total: number
  sent: number
}

export const accountUsersAPI = createApi({
  reducerPath: 'accountUsersAPI',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['accountUsers', 'currentUser'],
  endpoints: (build) => ({
    fetchAccountUsers: build.query<ListResponse<IAccountUser>, IQueryParams>({
      query: ({accountId, skip = 0, limit = 50}) => ({
        url: `/accounts/${accountId}/users`,
        params: {skip, limit},
      }),
      providesTags: (result) =>
        result
          ? [...result.data.map((m) => ({type: 'accountUsers' as const, id: m.id})), {type: 'accountUsers', id: 'LIST'}]
          : [{type: 'accountUsers', id: 'LIST'}],
    }),
    addAccountUser: build.mutation<IAccountUser, {accountId: string; body: IAccountMemberCreate}>({
      query: ({accountId, body}) => ({
        url: `/accounts/${accountId}/users`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['accountUsers', 'currentUser'],
    }),
    updateAccountUser: build.mutation<IAccountUser, {accountId: string; userId: string; body: IAccountMemberUpdate}>({
      query: ({accountId, userId, body}) => ({
        url: `/accounts/${accountId}/users/${userId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['accountUsers', 'currentUser'],
    }),
    deleteAccountUser: build.mutation<void, {accountId: string; userId: string}>({
      query: ({accountId, userId}) => ({
        url: `/accounts/${accountId}/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['accountUsers', 'currentUser'],
    }),
    inviteUsers: build.mutation<IAccountInviteResult, {accountId: string; body: {invites: IAccountInviteItem[]}}>({
      query: ({accountId, body}) => ({
        url: `/accounts/${accountId}/invites`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['accountUsers', 'currentUser'],
    }),
    acceptInvite: build.mutation<{status: string}, {token: string}>({
      query: ({token}) => ({
        url: `/accounts/invites/accept/${token}`,
        method: 'POST',
      }),
    }),
  }),
})
